// Client-side analytics service. The only thing in the app that talks to the
// `track-events` edge function. Composables and call sites should go through
// `useAnalytics()` (slice 4); this file is intentionally a plain TS module so
// it stays testable without a component tree.
//
// Responsibilities (ANALYTICS_PLAN → "analyticsService"):
//   * Queue + batch (20 events or 2s idle, whichever comes first; capped at
//     `MAX_BATCH_SIZE` per request to match the edge validator).
//   * Sessionize (`session_id` rotates after 30 min of inactivity).
//   * Mint and persist a stable `anonymous_id` per browser instance.
//   * Drop duplicate impressions within a session by caller-supplied key.
//   * Strip PII property keys before they leave the browser, and flag the
//     misuse via `logError` so the bug surfaces in Sentry (the plan is
//     deliberate: we want the bug, not the data).
//   * Flush on `visibilitychange→hidden` and `pagehide` via a keepalive
//     fetch — `navigator.sendBeacon` can't carry the Supabase gateway
//     headers, so keepalive-fetch is the practical equivalent.
//   * Spill failed batches to a bounded `localStorage` queue and drain it
//     on the next page load.
//
// Consent gating lives in the composable, not here. The service simply
// drops everything until `setEnabled(true)` is called.

import { SUPABASE_URL, SUPABASE_ANON_KEY, invokeWithTimeout } from './api/client';
import { logError } from '../utils/logger';
import { delay } from '../utils/delay';

export const EVENT_TYPES = [
    'impression',
    'interaction',
    'intent',
    'success',
    'failure',
] as const;

export type AnalyticsEventType = (typeof EVENT_TYPES)[number];

export type TrackInput = {
    eventName: string;
    eventType: AnalyticsEventType;
    flowId?: string;
    properties?: Record<string, unknown>;
    context?: Record<string, unknown>;
    /**
     * When set, suppresses duplicate enqueues within the current session
     * keyed by `${eventName}:${dedupeKey}`. Intended for impressions where
     * the same card may render many times.
     */
    dedupeKey?: string;
};

export type ReadyEvent = {
    event_id: string;
    event_name: string;
    event_type: AnalyticsEventType;
    flow_id: string | null;
    anonymous_id: string;
    session_id: string;
    properties: Record<string, unknown> | null;
    context: Record<string, unknown> | null;
    occurred_at: string;
};

export type SendResult = { ok: boolean };

export type AnalyticsServiceDeps = {
    now?: () => number;
    randomUUID?: () => string;
    storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
    send?: (events: ReadyEvent[]) => Promise<SendResult>;
    sendBeacon?: (events: ReadyEvent[]) => boolean;
    logError?: (error: unknown, context?: Record<string, string>) => void;
    /** Snapshot the current browsing context — route, viewport, locale, app version. */
    captureContext?: () => Record<string, unknown>;
    /** Flush trigger: queue length at which we flush synchronously. */
    batchFlushThreshold?: number;
    /** Idle delay before a queued event is flushed if nothing else triggers. */
    flushIntervalMs?: number;
    /** Inactivity period after which the session id rotates. */
    sessionTimeoutMs?: number;
    /** Cap on the localStorage retry queue. */
    retryQueueCap?: number;
    /** Delay between the first failed send and the one in-process retry. */
    retryDelayMs?: number;
};

// Storage keys: single source so the destructive-action wipe in slice 4 can
// reference the same names.
export const STORAGE_KEYS = {
    anonymousId: 'analytics:aid',
    sessionId: 'analytics:sid',
    sessionLastActivity: 'analytics:sid:last',
    retryQueue: 'analytics:queue',
} as const;

// Flush at 20 buffered events. Anything past `MAX_BATCH_SIZE` is split
// across requests to keep within the edge function's validator cap.
const DEFAULT_BATCH_FLUSH_THRESHOLD = 20;
const MAX_BATCH_SIZE = 50;
const DEFAULT_FLUSH_INTERVAL_MS = 2_000;
const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1_000;
const DEFAULT_RETRY_QUEUE_CAP = 100;
const DEFAULT_RETRY_DELAY_MS = 1_500;

// Property keys we refuse to ship. Stripping is silent in prod but loud in
// Sentry — the goal is to catch instrumentation bugs at their source, not
// reach for a workaround.
const PII_KEY_RE = /email|password|token|secret|api[_-]?key|phone|address/i;

export function createAnalyticsService(deps: AnalyticsServiceDeps = {}) {
    const now = deps.now ?? (() => Date.now());
    const randomUUID = deps.randomUUID ?? (() => crypto.randomUUID());
    const storage = deps.storage ?? localStorage;
    const send = deps.send ?? defaultSend;
    const reportError = deps.logError ?? logError;
    const captureContext = deps.captureContext ?? defaultCaptureContext;
    const batchFlushThreshold = deps.batchFlushThreshold ?? DEFAULT_BATCH_FLUSH_THRESHOLD;
    const flushIntervalMs = deps.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
    const sessionTimeoutMs = deps.sessionTimeoutMs ?? DEFAULT_SESSION_TIMEOUT_MS;
    const retryQueueCap = deps.retryQueueCap ?? DEFAULT_RETRY_QUEUE_CAP;
    const retryDelayMs = deps.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS;

    let enabled = false;
    const queue: ReadyEvent[] = [];
    let flushTimer: ReturnType<typeof setTimeout> | null = null;
    const dedupeKeysThisSession = new Set<string>();
    // Cached so we don't re-read storage on every event. Resets on session
    // rotation so a freshly minted session shares no impressions with the old.
    let currentSessionId: string | null = null;
    let lifecycleInstalled = false;
    // Latest Supabase session token, mirrored from `client.auth` in App.vue.
    // The default beacon attaches this so unload-time events get the correct
    // `user_id` stamped by the edge function — without it, the beacon's
    // anon-key fallback lands rows as anonymous and `forget_me` misses them.
    let currentAccessToken: string | null = null;

    // Default beacon lives inside the closure so it can read the cached
    // access token. Tests inject their own `sendBeacon` and don't touch this.
    const sendBeacon = deps.sendBeacon ?? defaultBeacon;
    function defaultBeacon(events: ReadyEvent[]): boolean {
        if (typeof navigator === 'undefined' || typeof fetch === 'undefined') {
            return false;
        }
        // `navigator.sendBeacon` can't set custom headers, and the Supabase
        // edge gateway needs both `apikey` and `Authorization`. Keepalive
        // -fetch is the pragmatic equivalent on every browser we care about
        // — it survives unload like beacon does, and lets us speak the
        // gateway's protocol.
        //
        // Use the user's access token when present so the edge function can
        // resolve `user_id` from the JWT. Falling back to the anon key keeps
        // anonymous visitors working.
        const token = currentAccessToken ?? SUPABASE_ANON_KEY;
        try {
            // fetch rejects asynchronously on network failure / unload abort;
            // the surrounding try/catch only sees synchronous throws. Swallow
            // the rejection here so a dropped beacon doesn't surface as an
            // unhandled "TypeError: Failed to fetch" in Sentry.
            fetch(`${SUPABASE_URL}/functions/v1/track-events`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    apikey: SUPABASE_ANON_KEY,
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(events),
                keepalive: true,
            }).catch(() => {});
            return true;
        } catch {
            return false;
        }
    }

    function setAccessToken(token: string | null): void {
        currentAccessToken = token;
    }

    function setEnabled(value: boolean): void {
        enabled = value;
        if (!value) {
            // When consent flips off we drop the in-memory tail. The retry
            // queue in localStorage is left alone — it's the previous session's
            // already-consented work, and clearing it belongs to "forget me".
            queue.length = 0;
            clearFlushTimer();
        }
    }

    function isEnabled(): boolean {
        return enabled;
    }

    function getAnonymousId(): string | null {
        try {
            return storage.getItem(STORAGE_KEYS.anonymousId);
        } catch (error) {
            reportError(error, { source: 'analytics:get-anonymous-id' });
            return null;
        }
    }

    function ensureAnonymousId(): string {
        const existing = getAnonymousId();
        if (existing != null) {
            return existing;
        }
        const fresh = randomUUID();
        storage.setItem(STORAGE_KEYS.anonymousId, fresh);
        return fresh;
    }

    function clearClientState(): void {
        // Best-effort wipe: the server-side "forget me" call has already run
        // by the time we get here, so a thrown storage exception must not
        // bubble up and make the user think their data wasn't deleted.
        try {
            storage.removeItem(STORAGE_KEYS.anonymousId);
            storage.removeItem(STORAGE_KEYS.sessionId);
            storage.removeItem(STORAGE_KEYS.sessionLastActivity);
            storage.removeItem(STORAGE_KEYS.retryQueue);
        } catch (error) {
            reportError(error, { source: 'analytics:clear-client-state' });
        }
        queue.length = 0;
        dedupeKeysThisSession.clear();
        currentSessionId = null;
        clearFlushTimer();
    }

    function resolveSessionId(): string {
        const last = Number(storage.getItem(STORAGE_KEYS.sessionLastActivity) ?? '0');
        const expired = !Number.isFinite(last) || last === 0 || now() - last > sessionTimeoutMs;
        let sid = storage.getItem(STORAGE_KEYS.sessionId);

        if (expired || sid == null) {
            sid = randomUUID();
            storage.setItem(STORAGE_KEYS.sessionId, sid);
            // A new session means fresh impressions — drop the dedupe memory
            // so a card seen 30 min ago is allowed to re-impress.
            if (currentSessionId != null && currentSessionId !== sid) {
                dedupeKeysThisSession.clear();
            }
        }
        currentSessionId = sid;
        storage.setItem(STORAGE_KEYS.sessionLastActivity, String(now()));
        return sid;
    }

    function track(input: TrackInput): void {
        // Analytics is a side channel: every synchronous failure here
        // (localStorage quota, disabled storage, missing `crypto.randomUUID`,
        // throwing `captureContext`) gets reported to Sentry and absorbed.
        // The hot-path callers in LoginModal, GameForm, Game.vue etc. assume
        // track is fire-and-forget — a thrown exception there would mask
        // success outcomes with the wrong error message.
        try {
            if (!enabled) {
                return;
            }

            // Resolve the session BEFORE the dedupe check — rotation clears the
            // dedupe set, so a card that was deduped in a long-gone session
            // should be free to impress again in a new one.
            const anonymousId = ensureAnonymousId();
            const sessionId = resolveSessionId();

            const dedupeMemo = input.dedupeKey != null
                ? `${input.eventName}:${input.dedupeKey}`
                : null;

            if (dedupeMemo != null && dedupeKeysThisSession.has(dedupeMemo)) {
                return;
            }

            const sanitizedProperties = sanitize(input.properties);
            const sanitizedContext = sanitize(input.context);

            const event: ReadyEvent = {
                event_id: randomUUID(),
                event_name: input.eventName,
                event_type: input.eventType,
                flow_id: input.flowId ?? null,
                anonymous_id: anonymousId,
                session_id: sessionId,
                properties: sanitizedProperties,
                context: mergeContext(captureContext(), sanitizedContext),
                occurred_at: new Date(now()).toISOString(),
            };

            queue.push(event);
            if (dedupeMemo != null) {
                dedupeKeysThisSession.add(dedupeMemo);
            }

            if (queue.length >= batchFlushThreshold) {
                void flush();
            } else {
                scheduleFlush();
            }
        } catch (error) {
            reportError(error, { source: 'analytics:track' });
        }
    }

    function scheduleFlush(): void {
        if (flushTimer != null) {
            return;
        }
        flushTimer = setTimeout(() => {
            flushTimer = null;
            void flush();
        }, flushIntervalMs);
    }

    function clearFlushTimer(): void {
        if (flushTimer != null) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
    }

    async function flush(): Promise<void> {
        try {
            clearFlushTimer();
            if (queue.length === 0) {
                return;
            }

            // Drain into request-sized chunks up front. New events that arrive
            // mid-flush will be picked up on the next scheduled flush.
            const drained = queue.splice(0, queue.length);
            const batches = chunk(drained, MAX_BATCH_SIZE);
            const remaining: ReadyEvent[] = [];

            for (let i = 0; i < batches.length; i++) {
                const batch = batches[i];
                const ok = await sendWithOneRetry(batch);
                if (!ok) {
                    // Bail on first failure: any unsent batches go to the retry
                    // queue together so order is preserved when we drain on next
                    // load.
                    remaining.push(...batch);
                    for (let j = i + 1; j < batches.length; j++) {
                        remaining.push(...batches[j]);
                    }
                    break;
                }
            }

            if (remaining.length > 0) {
                persistRetryQueue(remaining);
            }
        } catch (error) {
            reportError(error, { source: 'analytics:flush' });
        }
    }

    async function sendWithOneRetry(batch: ReadyEvent[]): Promise<boolean> {
        try {
            const first = await send(batch);
            if (first.ok) {
                return true;
            }
        } catch (error) {
            reportError(error, { source: 'analytics:send' });
        }

        await delay(retryDelayMs);

        try {
            const second = await send(batch);
            return second.ok;
        } catch (error) {
            reportError(error, { source: 'analytics:send-retry' });
            return false;
        }
    }

    function persistRetryQueue(events: ReadyEvent[]): void {
        const existing = readRetryQueue();
        const combined = existing.concat(events);
        // Drop-oldest if we exceed the cap. Keeps the most recent events
        // because users are usually more interested in "what just happened"
        // than week-old impressions.
        const trimmed = combined.length > retryQueueCap
            ? combined.slice(combined.length - retryQueueCap)
            : combined;
        try {
            storage.setItem(STORAGE_KEYS.retryQueue, JSON.stringify(trimmed));
        } catch (error) {
            reportError(error, { source: 'analytics:persist-retry-queue' });
        }
    }

    function readRetryQueue(): ReadyEvent[] {
        const raw = storage.getItem(STORAGE_KEYS.retryQueue);
        if (raw == null) {
            return [];
        }
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? (parsed as ReadyEvent[]) : [];
        } catch {
            // Corrupt blob — drop it, we'd rather lose a few events than
            // brick analytics on every load.
            storage.removeItem(STORAGE_KEYS.retryQueue);
            return [];
        }
    }

    async function drainPersistedQueue(): Promise<void> {
        try {
            const persisted = readRetryQueue();
            if (persisted.length === 0) {
                return;
            }
            // Clear up front: if the network drops mid-drain we'll re-spill the
            // remainder below. Avoids a duplicate retry queue snapshotting itself.
            storage.removeItem(STORAGE_KEYS.retryQueue);

            const batches = chunk(persisted, MAX_BATCH_SIZE);
            const remaining: ReadyEvent[] = [];
            for (let i = 0; i < batches.length; i++) {
                const batch = batches[i];
                const ok = await sendWithOneRetry(batch);
                if (!ok) {
                    remaining.push(...batch);
                    for (let j = i + 1; j < batches.length; j++) {
                        remaining.push(...batches[j]);
                    }
                    break;
                }
            }
            if (remaining.length > 0) {
                persistRetryQueue(remaining);
            }
        } catch (error) {
            reportError(error, { source: 'analytics:drain-persisted-queue' });
        }
    }

    function flushOnUnload(): void {
        try {
            clearFlushTimer();
            if (queue.length === 0) {
                return;
            }
            const drained = queue.splice(0, queue.length);
            const batches = chunk(drained, MAX_BATCH_SIZE);
            const spilled: ReadyEvent[] = [];
            for (const batch of batches) {
                const ok = sendBeacon(batch);
                if (!ok) {
                    spilled.push(...batch);
                }
            }
            if (spilled.length > 0) {
                persistRetryQueue(spilled);
            }
        } catch (error) {
            reportError(error, { source: 'analytics:flush-on-unload' });
        }
    }

    function installLifecycleHandlers(): void {
        try {
            if (lifecycleInstalled || typeof window === 'undefined') {
                return;
            }
            lifecycleInstalled = true;
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    flushOnUnload();
                }
            });
            window.addEventListener('pagehide', () => {
                flushOnUnload();
            });
        } catch (error) {
            reportError(error, { source: 'analytics:install-lifecycle-handlers' });
        }
    }

    function sanitize(input: Record<string, unknown> | undefined): Record<string, unknown> | null {
        if (input == null) {
            return null;
        }
        const cleaned: Record<string, unknown> = {};
        const stripped: string[] = [];
        for (const [key, value] of Object.entries(input)) {
            if (PII_KEY_RE.test(key)) {
                stripped.push(key);
            } else {
                cleaned[key] = value;
            }
        }
        if (stripped.length > 0) {
            // Report the key names only — values would defeat the point.
            reportError(new Error('Analytics property names matched the PII deny-list and were stripped'), {
                source: 'analytics:pii-guard',
                strippedKeys: stripped.join(','),
            });
        }
        return cleaned;
    }

    return {
        setEnabled,
        isEnabled,
        setAccessToken,
        track,
        flush,
        flushOnUnload,
        getAnonymousId,
        ensureAnonymousId,
        clearClientState,
        installLifecycleHandlers,
        drainPersistedQueue,
        // Exposed for tests / debugging only — not part of the public API.
        _peekQueue: (): readonly ReadyEvent[] => queue,
    };
}

export type AnalyticsService = ReturnType<typeof createAnalyticsService>;

export const analyticsService = createAnalyticsService();

function mergeContext(
    base: Record<string, unknown>,
    overlay: Record<string, unknown> | null,
): Record<string, unknown> | null {
    if (overlay == null) {
        return Object.keys(base).length > 0 ? base : null;
    }
    return { ...base, ...overlay };
}

function chunk<T>(items: T[], size: number): T[][] {
    if (items.length <= size) {
        return [items];
    }
    const out: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        out.push(items.slice(i, i + size));
    }
    return out;
}

async function defaultSend(events: ReadyEvent[]): Promise<SendResult> {
    const { error } = await invokeWithTimeout('track-events', { body: events });
    return { ok: error == null };
}

function defaultCaptureContext(): Record<string, unknown> {
    if (typeof window === 'undefined') {
        return {};
    }
    const ctx: Record<string, unknown> = {};
    if (typeof window.location !== 'undefined') {
        ctx.path = window.location.pathname;
    }
    if (typeof window.innerWidth === 'number' && typeof window.innerHeight === 'number') {
        ctx.viewport = `${window.innerWidth}x${window.innerHeight}`;
    }
    if (typeof navigator !== 'undefined' && typeof navigator.language === 'string') {
        ctx.locale = navigator.language;
    }
    return ctx;
}
